import './StationManagement.css';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
// Components
import StationTableItem from './component/StationTableItem';
import StationAppendSideModal from './component/StationAppendSideModal';
import StationUpdateSideModal from './component/StationUpdateSideModal';
import StationDeleteModal from './component/StationDeleteModal';
import StationDetailModal from './component/StationDetailModal';
import SearchBox from '../../components/SearchBox/SearchBox';
import { SkeletonTable } from '../../components/Skeleton';
import Pagination from '../../components/Pagination/Pagination';
// Icons
import ExcelIcon from '../../assets/icons/excel.svg?react';
import PlusIcon from '../../assets/icons/plus.svg?react';
// Hooks
import useStationsSimple from '../../hooks/useStationsSimple';
// React Query
import { useQueryClient } from '@tanstack/react-query';

import { useSearchParams } from 'react-router-dom';

const StationManagement = () => {
  // Refs
  const selectedFilterRef = useRef('');
  const searchValueRef = useRef('');
  // States
  const [canSearch, setCanSearch] = useState(false);
  const [keywordInput, setKeywordInput] = useState(''); // 입력 중인 키워드 (로컬 state)
  const [target, setTarget] = useState(null);
  const [editModalOn, setEditModalOn] = useState(false);
  const [deleteModalOn, setDeleteModalOn] = useState(false);
  const [appendModalOn, setAppendModalOn] = useState(false);
  const [stationDetailModal, setStationDetailModal] = useState(null);

  // URL Params
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('pageSize')) || 10;
  const searchQuery = searchParams.get('search') || '';
  const keyword = searchParams.get('Keyword') || '';
  const sortBy = searchParams.get('sortBy') || 'DrResourceSysId';
  const sortOrder = searchParams.get('sortOrder') || 'desc';
  
  // keywordInput을 URL의 keyword와 동기화 (초기값 설정)
  useEffect(() => {
    if (keyword) {
      setKeywordInput(keyword);
    } else {
      setKeywordInput('');
    }
  }, [keyword]);
  
  // Filter Params
  const cpoStationId = searchParams.get('CpoStationId') || '';
  const powerMeterContractNumber = searchParams.get('PowerMeterContractNumber') || '';
  const cpoName = searchParams.get('CpoName') || '';
  const cpoId = searchParams.get('CpoId') || '';
  const address = searchParams.get('Address') || '';
  const cpoStationName = searchParams.get('CpoStationName') || '';
  const drResourceId = searchParams.get('DrResourceId') || '';

  // React Query Client
  const queryClient = useQueryClient();

  // Query Params Memoization
  const queryParams = useMemo(() => {
    const params = {
      Page: currentPage,
      PageSize: pageSize,
      OrderBy: sortBy,
      OrderDirection: sortOrder,
    };
    
    // Keyword 파라미터 추가
    if (keyword) {
      params.Keyword = keyword;
    }
    
    // Search Params
    if (cpoStationId) {
      params.CpoStationId = cpoStationId;
    } else if (powerMeterContractNumber) {
      params.PowerMeterContractNumber = powerMeterContractNumber;
    } else if (cpoName) {
      params.CpoName = cpoName;
    } else if (cpoId) {
      params.CpoId = cpoId;
    } else if (address) {
      params.Address = address;
    } else if (cpoStationName) {
      params.CpoStationName = cpoStationName;
    } else if (drResourceId) {
      params.DrResourceId = drResourceId;
    } else if (searchQuery) {
      params.search = searchQuery;
    }
    
    return params;
  }, [currentPage, pageSize, sortBy, sortOrder, keyword, searchQuery, cpoStationId, powerMeterContractNumber, cpoName, cpoId, address, cpoStationName, drResourceId]);

  // React Query
  const { 
    stations, 
    getDrResourceById,
    drResources,
    isLoading: isLoading, 
    error: error,
    totalCount,
  } = useStationsSimple(queryParams);

  const onStationDetail = (station) => {
    setStationDetailModal(station);
  } 

  // Success Handlers
  const onDeleteSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['stations'] });
    if (currentPage > 1 && stations.length === 1) {
      updateSearchParams({ page: (currentPage - 1).toString() });
    }
  }

  const onCloseDeleteModal = () => {
    setTarget(null);
    setDeleteModalOn(false);
  }

  const onAppendSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['stations'] });
    updateSearchParams({ page: '1' });
  }

  const onEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['stations'] });
  }

  const onEdit = (station) => {
    setEditModalOn(true);
    setTarget(station);
  }

  const onDelete = (station) => {
    setDeleteModalOn(true);
    setTarget({station: station});
  }

  const updateSearchParams = (newParams) => {
    const current = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...current, ...newParams });
  };

  const handlePageChange = (page) => {
    updateSearchParams({ page: page.toString() });
  };

  const handleSearch = (query, filterType) => {
    // Initialize New Params
    const newParams = { page: '1' };
    
    // Set Filter Type
    if (filterType && query) {
      newParams[filterType] = query;
    } else if (query) {
      // Set Default Search Param
      newParams.search = query;
    }
    
    // Remove All Search Params and Set New Params
    const current = Object.fromEntries(searchParams.entries());
    const filteredParams = Object.fromEntries(
      Object.entries(current).filter(([key]) => 
        !['search', 'CpoStationId', 'PowerMeterContractNumber', 'CpoName', 'CpoId', 'Address', 'CpoStationName', 'DrResourceId'].includes(key)
      )
    );
    
    setSearchParams({ ...filteredParams, ...newParams });
  };

  const handleSort = (field, order) => {
    updateSearchParams({ sortBy: field, sortOrder: order, page: '1' });
  };

  // Keyword 입력 핸들러 (로컬 state만 업데이트)
  const handleKeywordChange = (e) => {
    setKeywordInput(e.target.value);
  };

  // Keyword 입력에서 Enter 키 처리 (쿼리 실행)
  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = e.target.value.trim();
      const current = Object.fromEntries(searchParams.entries());
      
      if (value && value.length >= 2) {
        setSearchParams({ ...current, Keyword: value, page: '1' });
      } else {
        // 빈 값이면 Keyword 파라미터 제거
        const { Keyword, ...rest } = current;
        setSearchParams({ ...rest, page: '1' });
      }
    }
  };
  

  return (
    <div className="station-management-container page-container">      
      <div className="station-management-content">

        <div className="flex justify-between align-center mb-10">
          <div className="w-half flex">
            <input 
              type="text" 
              placeholder="키워드 검색 (2자 이상) - 자원이름, 충전소이름, 식별번호, 주소, CPO이름, 계량기계약번호" 
              className="input-primary flex-1 text-b2-400" 
              value={keywordInput}
              onChange={handleKeywordChange}
              onKeyDown={handleKeywordKeyDown}
            />
          </div>
          <div className="flex justify-end gap-5">
            {/* <SearchBox 
              placeholder="검색" 
              filters={[
                {id: 'CpoStationId', label: '충전소 식별자'}, 
                {id: 'PowerMeterContractNumber', label: '계량기 계약번호'}, 
                {id: 'CpoName', label: 'CPO 이름'},
                {id: 'CpoId', label: 'CPO 식별자'},
                {id: "Address", label: '충전소 주소'},
                {id: "CpoStationName", label: '충전소 이름'},
                {id: "DrResourceId", label: 'DR 자원'},
              ]} 
              onFilterChange={() => {}} 
              onSearch={handleSearch} 
              defaultValue={cpoStationId || powerMeterContractNumber || cpoName || cpoId || address || cpoStationName || drResourceId || searchQuery}
              selectedFilterRef={selectedFilterRef}
              searchValueRef={searchValueRef}
              initialSelectedFilter={cpoStationId ? 'CpoStationId' : powerMeterContractNumber ? 'PowerMeterContractNumber' : cpoName ? 'CpoName' : cpoId ? 'CpoId' : address ? 'Address' : cpoStationName ? 'CpoStationName' : drResourceId ? 'DrResourceId' : ''}
              onSearchStateChange={setCanSearch}
            /> */}
            {/* <button 
              className={`btn plain btn-style-2 px-10 py-5 ${!canSearch ? 'opacity-50 cursor-not-allowed' : ''}`} 
              onClick={() => canSearch && handleSearch(searchValueRef.current, selectedFilterRef.current)}
              disabled={!canSearch}
            >
              검색
            </button> */}
            {/* <button className="btn big-btn btn-green-700 flex align-center gap-3" onClick={() => {}}><ExcelIcon /> 스프레드 시트 다운로드</button> */}
            <button className="btn big-btn btn-primary flex align-center gap-3" onClick={() => setAppendModalOn(true)}><PlusIcon /> 충전소 추가</button>
          </div>
        </div>

        <div className="primary-table">
          {
            isLoading && (
              <SkeletonTable rows={8} columns={8} />
            )
          }
          {error && (
            <div className="error-container">
              <p>데이터를 불러오는데 실패했습니다</p>
            </div>
          )}
          {!isLoading && !error && (
          <table>
            <thead>
              <tr>
                <th>번호</th>
                <th>DR자원</th>
                <th>충전소 이름</th>
                <th>충전소 식별자</th>
                <th>충전소 주소</th>
                <th>충전사업자</th>
                <th>충전사업자 식별자</th>
                <th>계량기 계약번호</th>
                <th></th>
              </tr>
            </thead>
              <tbody>
              {stations.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    충전소 데이터가 없습니다.
                  </td>
                </tr>
              ) : (
                stations.map((station, idx) => {
                  // Generate Unique ID
                  const uniqueId = station.StationSysId || station.CpoStationId || `temp-${currentPage}-${idx}`;
                  const displayIndex = (currentPage - 1) * pageSize + idx + 1;
                  
                  return (
                    <StationTableItem 
                      key={`${uniqueId}-${currentPage}-${idx}`} 
                      station={station} 
                      idx={displayIndex} 
                      onEdit={onEdit} 
                      onDelete={onDelete} 
                      onStationDetail={onStationDetail} 
                      drResourceInfo={getDrResourceById(station.DrResourceSysId)}
                    />
                  );
                })
              )}
            </tbody>
            </table>
          )}
        </div>
        {/* Pagination */}
        {!isLoading && !error && (
          <div className="pagination-wrapper">
          <Pagination
            currentPage={currentPage}
              totalPages={Math.max(1, Math.ceil(totalCount / pageSize))}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {appendModalOn && <StationAppendSideModal onClose={() => setAppendModalOn(false)} onSuccess={onAppendSuccess} />}
      {editModalOn && <StationUpdateSideModal onClose={() => setEditModalOn(false)} target={target} onSuccess={onEditSuccess} />}
      {deleteModalOn && <StationDeleteModal onClose={onCloseDeleteModal} station={target?.station} onDeleteSuccess={onDeleteSuccess} />}
      {stationDetailModal && <StationDetailModal onClose={() => setStationDetailModal(null)} station={stationDetailModal} />}
    </div>
  );
};

export default StationManagement; 